import { useEffect, useState } from 'react';
import { onIdTokenChanged, type Auth, type User } from 'firebase/auth';
import { setTenantId } from '@elka/firebase';

/**
 * Resolution of the signed-in user's tenant custom claims.
 *
 * - `loading`       — waiting for the first id-token result.
 * - `signed-out`    — no Firebase user.
 * - `unprovisioned` — authenticated, but no `tenantId` claim (office user not
 *                     yet onboarded, or a worker before linkWorkerByPhone runs).
 * - `ready`         — `tenantId` claim present; the session tenant is set.
 */
export type TenantClaimsState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'unprovisioned'; user: User }
  | {
      status: 'ready';
      user: User;
      tenantId: string;
      role: 'admin' | 'user' | null;
      /** Backend `superAdmin` custom claim (payslips + audit-log gate). */
      superAdmin: boolean;
    };

/**
 * Guards a single forced token refresh per uid. When claims are freshly minted
 * server-side (onboarding script / linkWorkerByPhone) the cached id token is
 * stale, so we force ONE refresh to pick them up. The Set stops that refresh
 * from looping forever for a user who genuinely has no claim (unprovisioned).
 */
const forcedRefreshUids = new Set<string>();
/** Uids whose claim poll is in flight (see the loop in the effect). */
const pendingUids = new Set<string>();
const CLAIM_POLL_ATTEMPTS = 6;
const CLAIM_POLL_MS = 2000;

/**
 * Guards a single automatic retry per uid when getIdTokenResult() throws. A
 * transient failure (e.g. a network blip at app open) must not be mistaken for
 * an 'unprovisioned' user, so we retry ONCE; this Set stops the retry looping.
 */
const errorRetriedUids = new Set<string>();

function readClaims(claims: Record<string, unknown>): {
  tenantId?: string;
  role: 'admin' | 'user' | null;
  superAdmin: boolean;
} {
  const tenantId =
    typeof claims.tenantId === 'string' && claims.tenantId.length > 0 ? claims.tenantId : undefined;
  const role = claims.role === 'admin' || claims.role === 'user' ? claims.role : null;
  const superAdmin = claims.superAdmin === true;
  return { tenantId, role, superAdmin };
}

export function useTenantClaims(auth: Auth): TenantClaimsState {
  const [state, setState] = useState<TenantClaimsState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (cancelled) return;

      if (!user) {
        setState({ status: 'signed-out' });
        return;
      }

      try {
        let result = await user.getIdTokenResult();
        let { tenantId, role, superAdmin } = readClaims(result.claims);

        // Forced refresh(es), once per uid, to pick up just-minted claims. A
        // brand-new office user gets claims from the assignTenantClaimsOnCreate
        // trigger a few seconds after the first sign-in, so for those accounts we
        // poll briefly before giving up. An older account with no claim is
        // genuinely unprovisioned — no trigger is about to fire for it, so one
        // refresh (to rule out a merely stale cached token) is enough.
        const creationTime = user.metadata?.creationTime;
        const createdAtMs = creationTime ? Date.parse(creationTime) : NaN;
        const isNewAccount = Number.isFinite(createdAtMs) && Date.now() - createdAtMs < 2 * 60_000;

        if (!tenantId && !forcedRefreshUids.has(user.uid)) {
          forcedRefreshUids.add(user.uid);
          if (isNewAccount) {
            pendingUids.add(user.uid);
            try {
              for (
                let attempt = 0;
                attempt < CLAIM_POLL_ATTEMPTS && !tenantId && !cancelled;
                attempt++
              ) {
                if (attempt > 0) await new Promise((r) => setTimeout(r, CLAIM_POLL_MS));
                result = await user.getIdTokenResult(true);
                ({ tenantId, role, superAdmin } = readClaims(result.claims));
              }
            } finally {
              pendingUids.delete(user.uid);
            }
          } else {
            result = await user.getIdTokenResult(true);
            ({ tenantId, role, superAdmin } = readClaims(result.claims));
          }
        } else if (!tenantId && pendingUids.has(user.uid)) {
          // Re-entered by our own forced refresh while the poll above is still
          // running — let that loop decide, don't flash the unprovisioned screen.
          return;
        }

        if (cancelled) return;

        if (!tenantId) {
          setState({ status: 'unprovisioned', user });
          return;
        }

        // Set the session tenant BEFORE returning 'ready' so any Firestore query
        // that mounts on the next render resolves against the right tenant.
        setTenantId(tenantId);
        setState({ status: 'ready', user, tenantId, role, superAdmin });
      } catch {
        if (cancelled) return;
        // A transient getIdTokenResult() failure must not be mistaken for
        // 'unprovisioned'. Retry ONCE per uid; only if the retry also throws do
        // we fall to the not-provisioned screen (its "בדוק שוב" button is the
        // manual recovery from there).
        if (!errorRetriedUids.has(user.uid)) {
          errorRetriedUids.add(user.uid);
          try {
            const result = await user.getIdTokenResult();
            const { tenantId, role, superAdmin } = readClaims(result.claims);
            if (cancelled) return;
            if (tenantId) {
              setTenantId(tenantId);
              setState({ status: 'ready', user, tenantId, role, superAdmin });
              return;
            }
          } catch {
            // fall through to unprovisioned below
          }
        }
        if (!cancelled) setState({ status: 'unprovisioned', user });
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [auth]);

  return state;
}

/**
 * Force a token refresh and report whether a `tenantId` claim is now present.
 * Backs the "check again" button on the not-provisioned screen (a just-onboarded
 * user can self-serve instead of fully reloading).
 */
export async function retryClaims(user: User): Promise<boolean> {
  const result = await user.getIdTokenResult(true);
  return typeof result.claims.tenantId === 'string' && result.claims.tenantId.length > 0;
}
